// src/services/dataService.ts
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  setDoc,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { getCurrentUserId } from './auth';
import { DeliveryRecord, Expense, DeliveryPlatform, ExpenseCategory, RestDay } from '../types';
import { getRankingSettings, submitRankingRecord } from './rankingService';
import { calculateActualWorkingMinutes } from '../types';

// ユーザーIDを取得
const getUserId = (): string => {
  return getCurrentUserId();
};

// コレクションパスを生成
const getUserCollection = (collectionName: string) => {
  const userId = getUserId();
  return `users/${userId}/${collectionName}`;
};

// エラーハンドリング
const handleError = (error: any, operation: string) => {
  console.error(`${operation}エラー:`, error);
  throw new Error(`${operation}に失敗しました`);
};

const removeUndefinedFields = <T extends Record<string, any>>(data: T): T => {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined)
  ) as T;
};

// デフォルトデータ
const DEFAULT_EXPENSE_CATEGORIES: Omit<ExpenseCategory, 'id'>[] = [
  { name: 'ガソリン代', icon: '⛽', color: '#EF4444', isActive: true, createdAt: Date.now() },
  { name: '駐車料金', icon: '🅿️', color: '#EF4444', isActive: true, createdAt: Date.now() },
  { name: '車両維持費', icon: '🔧', color: '#EF4444', isActive: true, createdAt: Date.now() },
  { name: '車両保険料', icon: '🛡️', color: '#EF4444', isActive: true, createdAt: Date.now() },
  { name: '通信費', icon: '📱', color: '#3B82F6', isActive: true, createdAt: Date.now() },
  { name: '消耗品費', icon: '🛒', color: '#8B5CF6', isActive: true, createdAt: Date.now() },
  { name: '食費', icon: '🍱', color: '#F59E0B', isActive: true, createdAt: Date.now() },
  { name: '交通費', icon: '🚗', color: '#10B981', isActive: true, createdAt: Date.now() },
  { name: '水道光熱費', icon: '💡', color: '#3B82F6', isActive: true, createdAt: Date.now() },
  { name: '地代家賃', icon: '🏠', color: '#8B5CF6', isActive: true, createdAt: Date.now() },
  { name: '荷造運賃', icon: '📦', color: '#F59E0B', isActive: true, createdAt: Date.now() },
  { name: '研修費', icon: '🎓', color: '#10B981', isActive: true, createdAt: Date.now() },
  { name: '福利厚生費', icon: '🥗', color: '#3B82F6', isActive: true, createdAt: Date.now() },
  { name: '雑費', icon: '💰', color: '#888888', isActive: true, createdAt: Date.now() },
];

const DEFAULT_PLATFORMS: Omit<DeliveryPlatform, 'id'>[] = [
  { name: '🐸', color: '#10B981', isActive: true, createdAt: Date.now() },
  { name: '🥫', color: '#EF4444', isActive: true, createdAt: Date.now() },
  { name: '📚', color: '#8B5CF6', isActive: true, createdAt: Date.now() },
  { name: '🦌', color: '#3B82F6', isActive: true, createdAt: Date.now() },
];

// 初期データ作成
export const initializeDefaultData = async (): Promise<void> => {
  try {
    const existingCategories = await getAllExpenseCategories();
    
    if (existingCategories.length === 0) {
      const batch = writeBatch(db);
      DEFAULT_EXPENSE_CATEGORIES.forEach((category) => {
        const docRef = doc(collection(db, getUserCollection('expenseCategories')));
        batch.set(docRef, category);
      });
      await batch.commit();
    }

    const existingPlatforms = await getAllPlatforms();
    
    if (existingPlatforms.length === 0) {
      const batch = writeBatch(db);
      DEFAULT_PLATFORMS.forEach((platform) => {
        const docRef = doc(collection(db, getUserCollection('platforms')));
        batch.set(docRef, platform);
      });
      await batch.commit();
    }
  } catch (error) {
    console.error('初期データの作成に失敗:', error);
  }
};

// ランキングへの自動送信処理
const autoSubmitToRanking = async (delivery: DeliveryRecord): Promise<void> => {
  try {
    // ランキング設定を確認
    const settings = await getRankingSettings();
    
    // ランキング機能がOFFの場合は何もしない
    if (!settings.isEnabled) {
      return;
    }

    // その日のその配達プラットフォームの全記録を取得
    const allDeliveries = await getDeliveriesByDateRange(delivery.date, delivery.date);
    const platformDeliveries = allDeliveries.filter(d => d.platform === delivery.platform);

    // 集計
    const revenue = platformDeliveries.reduce((sum, r) => sum + r.totalRevenue, 0);
    const deliveryCount = platformDeliveries.reduce((sum, r) => sum + r.deliveryCount, 0);
    const workingMinutes = calculateActualWorkingMinutes(platformDeliveries);

    // ランキングに送信
    await submitRankingRecord({
      userName: settings.userName,
      date: delivery.date,
      revenue,
      deliveryCount,
      workingMinutes,
      platform: delivery.platform,
      createdAt: Date.now()
    });

    console.log('ランキングに自動送信しました:', delivery.date, delivery.platform);
  } catch (error) {
    console.error('ランキング自動送信エラー:', error);
    // エラーが発生しても配達記録の保存は成功しているので、エラーを投げない
  }
};

// ========== 配達記録 ==========

export const addDelivery = async (delivery: Omit<DeliveryRecord, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, getUserCollection('deliveries')), removeUndefinedFields({
      ...delivery,
      createdAt: Date.now()
    }));
    
    // ランキングに自動送信
    await autoSubmitToRanking({ ...delivery, id: docRef.id } as DeliveryRecord);
    
    return docRef.id;
  } catch (error) {
    handleError(error, '配達記録の追加');
    throw error;
  }
};

export const updateDelivery = async (id: string, delivery: Partial<DeliveryRecord>): Promise<void> => {
  try {
    await updateDoc(doc(db, getUserCollection('deliveries'), id), removeUndefinedFields(delivery));
    
    // 更新後の完全なレコードを取得してランキングに送信
    if (delivery.date || delivery.platform || delivery.totalRevenue || delivery.deliveryCount) {
      const allDeliveries = await getAllDeliveries();
      const updatedDelivery = allDeliveries.find(d => d.id === id);
      if (updatedDelivery) {
        await autoSubmitToRanking(updatedDelivery);
      }
    }
  } catch (error) {
    handleError(error, '配達記録の更新');
  }
};

export const deleteDelivery = async (id: string): Promise<void> => {
  try {
    // 削除前にレコード情報を取得
    const allDeliveries = await getAllDeliveries();
    const deletedDelivery = allDeliveries.find(d => d.id === id);
    
    await deleteDoc(doc(db, getUserCollection('deliveries'), id));
    
    // 削除後、その日のデータを再送信
    if (deletedDelivery) {
      await autoSubmitToRanking(deletedDelivery);
    }
  } catch (error) {
    handleError(error, '配達記録の削除');
  }
};

export const getAllDeliveries = async (): Promise<DeliveryRecord[]> => {
  try {
    const q = query(collection(db, getUserCollection('deliveries')), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeliveryRecord));
  } catch (error) {
    handleError(error, '配達記録の取得');
    return [];
  }
};

export const getDeliveriesByDateRange = async (startDate: string, endDate: string): Promise<DeliveryRecord[]> => {
  try {
    const q = query(
      collection(db, getUserCollection('deliveries')),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeliveryRecord));
  } catch (error) {
    handleError(error, '配達記録の取得');
    return [];
  }
};

// ========== 経費 ==========

export const addExpense = async (expense: Omit<Expense, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, getUserCollection('expenses')), {
      ...expense,
      createdAt: Date.now()
    });
    return docRef.id;
  } catch (error) {
    handleError(error, '支出の追加');
    throw error;
  }
};

export const updateExpense = async (id: string, expense: Partial<Expense>): Promise<void> => {
  try {
    await updateDoc(doc(db, getUserCollection('expenses'), id), expense);
  } catch (error) {
    handleError(error, '支出の更新');
  }
};

export const deleteExpense = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, getUserCollection('expenses'), id));
  } catch (error) {
    handleError(error, '支出の削除');
  }
};

export const getAllExpenses = async (): Promise<Expense[]> => {
  try {
    const q = query(collection(db, getUserCollection('expenses')), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Expense));
  } catch (error) {
    handleError(error, '支出の取得');
    return [];
  }
};

// ========== プラットフォーム ==========

export const addPlatform = async (platform: Omit<DeliveryPlatform, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, getUserCollection('platforms')), {
      ...platform,
      createdAt: Date.now()
    });
    return docRef.id;
  } catch (error) {
    handleError(error, 'プラットフォームの追加');
    throw error;
  }
};

export const updatePlatform = async (id: string, platform: Partial<DeliveryPlatform>): Promise<void> => {
  try {
    await updateDoc(doc(db, getUserCollection('platforms'), id), platform);
  } catch (error) {
    handleError(error, 'プラットフォームの更新');
  }
};

export const deletePlatform = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, getUserCollection('platforms'), id));
  } catch (error) {
    handleError(error, 'プラットフォームの削除');
  }
};

export const getAllPlatforms = async (): Promise<DeliveryPlatform[]> => {
  try {
    const snapshot = await getDocs(collection(db, getUserCollection('platforms')));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeliveryPlatform));
  } catch (error) {
    handleError(error, 'プラットフォームの取得');
    return [];
  }
};

// ========== 経費カテゴリ ==========

export const addExpenseCategory = async (category: Omit<ExpenseCategory, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, getUserCollection('expenseCategories')), {
      ...category,
      createdAt: Date.now()
    });
    return docRef.id;
  } catch (error) {
    handleError(error, 'カテゴリの追加');
    throw error;
  }
};

export const updateExpenseCategory = async (id: string, category: Partial<ExpenseCategory>): Promise<void> => {
  try {
    await updateDoc(doc(db, getUserCollection('expenseCategories'), id), category);
  } catch (error) {
    handleError(error, 'カテゴリの更新');
  }
};

export const deleteExpenseCategory = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, getUserCollection('expenseCategories'), id));
  } catch (error) {
    handleError(error, 'カテゴリの削除');
  }
};

export const getAllExpenseCategories = async (): Promise<ExpenseCategory[]> => {
  try {
    const snapshot = await getDocs(collection(db, getUserCollection('expenseCategories')));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExpenseCategory));
  } catch (error) {
    handleError(error, 'カテゴリの取得');
    return [];
  }
};

// ========== 休日 ==========

export const addRestDay = async (restDay: RestDay): Promise<void> => {
  try {
    await addDoc(collection(db, getUserCollection('restDays')), restDay);
  } catch (error) {
    handleError(error, '休日の追加');
  }
};

export const deleteRestDay = async (date: string): Promise<void> => {
  try {
    const q = query(collection(db, getUserCollection('restDays')), where('date', '==', date));
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => batch.delete(doc.ref));
    await batch.commit();
  } catch (error) {
    handleError(error, '休日の削除');
  }
};

export const getAllRestDays = async (): Promise<RestDay[]> => {
  try {
    const snapshot = await getDocs(collection(db, getUserCollection('restDays')));
    return snapshot.docs.map(doc => doc.data() as RestDay);
  } catch (error) {
    handleError(error, '休日の取得');
    return [];
  }
};

// ========== 月次目標 ==========

export const getMonthlyTarget = async (): Promise<number> => {
  try {
    const userId = getUserId();
    const docRef = doc(db, `users/${userId}/settings`, 'monthlyTarget');
    const snapshot = await getDocs(query(collection(db, `users/${userId}/settings`)));
    const targetDoc = snapshot.docs.find(d => d.id === 'monthlyTarget');
    return targetDoc?.data()?.value || 150000;
  } catch (error) {
    return 150000;
  }
};

export const setMonthlyTarget = async (target: number): Promise<void> => {
  try {
    const userId = getUserId();
    const docRef = doc(db, `users/${userId}/settings`, 'monthlyTarget');
    await setDoc(docRef, { value: target, updatedAt: Date.now() }, { merge: true });
  } catch (error) {
    handleError(error, '月次目標の設定');
  }
};

export const saveRestDays = async (dates: Date[]): Promise<void> => {
  try {
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const existingRestDays = await getAllRestDays();
    const batch = writeBatch(db);
    
    // 既存の休日を削除
    const deletePromises = existingRestDays.map(async (day) => {
      const q = query(collection(db, getUserCollection('restDays')), where('date', '==', day.date));
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(doc => batch.delete(doc.ref));
    });
    
    await Promise.all(deletePromises);
    
    // 新しい休日を追加
    dates.forEach(date => {
      const docRef = doc(collection(db, getUserCollection('restDays')));
      batch.set(docRef, { 
        date: formatDate(date),
        reason: '' 
      });
    });
    
    await batch.commit();
  } catch (error) {
    handleError(error, '休日の保存');
  }
};

export const saveRestDaysForMonth = async (
  dates: Date[],
  year: number,
  month: number
): Promise<void> => {
  try {
    const formatDate = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;
    const existingRestDays = await getAllRestDays();
    const batch = writeBatch(db);

    const deletePromises = existingRestDays
      .filter((day) => day.date.startsWith(monthPrefix))
      .map(async (day) => {
        const q = query(collection(db, getUserCollection('restDays')), where('date', '==', day.date));
        const snapshot = await getDocs(q);
        snapshot.docs.forEach(doc => batch.delete(doc.ref));
      });

    await Promise.all(deletePromises);

    dates
      .filter((date) => date.getFullYear() === year && date.getMonth() === month)
      .forEach(date => {
        const docRef = doc(collection(db, getUserCollection('restDays')));
        batch.set(docRef, {
          date: formatDate(date),
          reason: ''
        });
      });

    await batch.commit();
  } catch (error) {
    handleError(error, '休日の月別保存');
  }
};
